// Similarity on ?live=1 (Track 2 demo push, the last widget): Ben's own scoring
// (`modules/similarity-view.ts`, unchanged) run over the shared group (`live/group-players.ts`) —
// her league's Stats file for the season shown — instead of the snapshot.
//
// The decided defaults (Prateek, 2026-10-15, track2-plan §9 "Demo push"), as Filter / Comparison:
// - The group = her league's file, her current season (Q-010 parked: her league only, so one
//   gender); the season picker = that league's built seasons; movers who left are out (Q-007).
// - Her numbers = her whole season in that league (her "total" row), as the snapshot's season
//   stats are. Read from the file directly, so a mover still sees her own games.
// - Every candidate is read in the same season (one file), where the snapshot reads each at her
//   own current season — the same thing for a current-season group.
// - A Position chip → candidates are minutes AT each position (Ben's Position rule, rules.md), a
//   row per position, labelled "at Full Back" (as Ranking / Filter / Comparison).
// - The radar's spokes are scaled as the live Radar's (minutes at the template, Q-009 / Q-011 B).

import type { StatsFile } from "@catbento/shared";
import type { PlayerId } from "../data/players.js";
import { compareTwoPlayers, metricRangesForAxes, MIN_SIMILARITY_PCT, SIMILARITY_RANGE_FILTERS, type SimilarityResult } from "../modules/similarity-view.js";
import { matchesFilters, type ActiveFilters, type KitPlayer, type RangeFilterConfig } from "../engines/stats-filter-kit.js";
import { groupRowsOf, LIVE_RANGE_FILTERS } from "./stats-files.js";
import type { GroupPlayer } from "./group-players.js";

/** The fixed columns every file row has (`groupRowsOf` adds them itself), so not asked for by name. */
const FIXED = new Set(["minutes", "nineties_played"]);

/** The metric keys to read from the file: the radar's axes only, so one renamed engine key can't
 *  break the widget (a key the file lacks throws in `groupRowsOf`). */
export function similarityMetricKeys(axisKeys: readonly string[]): string[] {
  return [...new Set(axisKeys.filter((k) => !FIXED.has(k)))];
}

/** The live chips, in Ben's add-menu order: the ones the files have data for (as Comparison's).
 *  League waits for Q-010 (the group already IS her league); Height, Weight and Preferred Foot stay
 *  hidden on real data (Q-013). Minutes: rules.md's chip for every module. */
export const LIVE_SIMILARITY_CHIPS = ["league", "position", "club", "nationality", "foot", "age", "height", "weight", "minutes"] as const;

/** Ben's absolute ranges + the Minutes chip. */
export const LIVE_SIMILARITY_RANGE_FILTERS: RangeFilterConfig = {
  ...SIMILARITY_RANGE_FILTERS,
  minutes: LIVE_RANGE_FILTERS.minutes!,
};

/**
 * One player's record in the snapshot's shape, straight from the file: her whole season
 * (`position` null) or only her minutes at `position`. Null = no such row (she didn't play here,
 * or not there). Not through `groupPlayers`, which leaves movers out: her OWN numbers are still
 * hers (as the live Radar's `herValuesAt`). Pure.
 */
export function playerRecord(
  file: StatsFile,
  playerId: PlayerId,
  position: string | null,
  metricKeys: string[],
  leagueLabel: string,
): GroupPlayer | null {
  const pick = position === null ? ({ kind: "total" } as const) : ({ kind: "group", positionGroup: position } as const);
  const row = groupRowsOf(file, pick, metricKeys).find((r) => r.playerId === playerId);
  if (!row) return null;
  const who = file.players[String(playerId)];
  if (!who) throw new Error(`Stats file ${file.competitionId}/${file.seasonId}: player ${playerId} has rows but no name`);
  const seasonCode = String(file.seasonId);
  return {
    id: String(playerId),
    playerId,
    name: who.name,
    // Her main position for a whole-season record — `herTemplate`'s rule, done by the caller.
    positionTemplate: row.positionTemplate ?? null,
    team: row.team ?? null,
    competition: leagueLabel,
    country: row.country ?? null,
    dob: row.dob ?? null,
    currentSeason: seasonCode,
    stats: row.stats,
    seasons: { [seasonCode]: { stats: row.stats } },
  };
}

export interface LiveSimilarityParams {
  /** Who she's compared with: the file's players (position rows when a Position chip is on). */
  candidates: Record<string, GroupPlayer>;
  /** Who each spoke's range comes from: the whole file (one row per player), whatever the chips —
   *  Ben's "range is a stable fact about the metric", never narrowed by the filters. */
  rangePopulation: Record<string, GroupPlayer>;
  base: GroupPlayer;
  season: string;
  axisKeys: readonly string[];
  activeFilters: ActiveFilters;
  rangeFilters: RangeFilterConfig;
}

/**
 * Ben's `similarityResultsFor`, step for step, over the live group. Two differences, both because
 * the group is keyed by id (not name, as the snapshot is): she is left out by id — every one of
 * her rows, so a Position chip never lists her against herself — and the ranges come from
 * `rangePopulation`, not the (maybe position-row) candidates. Pure.
 */
export function liveSimilarityResults({
  candidates,
  rangePopulation,
  base,
  season,
  axisKeys,
  activeFilters,
  rangeFilters,
}: LiveSimilarityParams): SimilarityResult[] {
  const rangeByKey = metricRangesForAxes(rangePopulation, season, axisKeys);
  const refPlayer = base as unknown as KitPlayer;
  const out: SimilarityResult[] = [];
  for (const pl of Object.values(candidates)) {
    if (pl.playerId === base.playerId) continue;
    if (!matchesFilters(pl as unknown as KitPlayer, activeFilters, refPlayer, rangeFilters)) continue;
    const cmp = compareTwoPlayers(base, season, pl, pl.currentSeason, axisKeys, rangeByKey);
    if (cmp && cmp.similarityPct > MIN_SIMILARITY_PCT) out.push(cmp);
  }
  out.sort((a, b) => b.similarityPct - a.similarityPct);
  return out;
}
